// Gemeinsame UI-Bausteine des E-Invoicing-Moduls.
// Zwei-Spuren-Statusprinzip (SAP-DRC/Pagero-Muster): Business-Status und
// Transport-Status sind ZWEI getrennte Chip-Familien und werden nie vermischt.
// Status-Farben sind Zustandsfarben (reserviert) — Serienfarben der Charts sind
// davon getrennt (validierte Palette in DashboardScreen).

import React from 'react';

/** Business-Status -> Chip-Ton. */
const BUSINESS_TONE = {
  received: 'blue', validated: 'blue', validation_failed: 'red',
  in_approval: 'amber', approved: 'green', rejected: 'red',
  posted_to_sap: 'green', archived: 'slate',
  draft: 'slate', generated: 'blue', sent: 'green',
};

/** Transport-/Behoerden-Status -> Chip-Ton. */
const TRANSPORT_TONE = {
  received: 'slate', delivered: 'green', submitted: 'blue', upo_received: 'green',
  rejected_platform: 'red', notifica_scarto: 'red',
};

const CHIP_CLS = (isDarkMode) => ({
  blue: isDarkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700',
  green: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
  amber: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
  red: isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700',
  slate: isDarkMode ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-600',
  violet: isDarkMode ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700',
});

export const Chip = ({ tone = 'slate', isDarkMode, children, title }) => (
  <span title={title} className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${CHIP_CLS(isDarkMode)[tone] || CHIP_CLS(isDarkMode).slate}`}>
    {children}
  </span>
);

export const BusinessChip = ({ state, isDarkMode, t }) => (
  <Chip tone={BUSINESS_TONE[state] || 'slate'} isDarkMode={isDarkMode} title={t('einvoicing.chip.business')}>
    {t(`einvoicing.state.${state}`)}
  </Chip>
);

export const TransportChip = ({ state, isDarkMode, t }) => {
  if (!state) return null;
  return (
    <Chip tone={TRANSPORT_TONE[state] || 'slate'} isDarkMode={isDarkMode} title={t('einvoicing.chip.transport')}>
      {t(`einvoicing.transport.${state}`)}
    </Chip>
  );
};

/** Formatbadge (XRechnung/ZUGFeRD/... ) — Lexoffice-Muster "E-Rechnung sofort erkennbar". */
export const FormatBadge = ({ format, isDarkMode }) => {
  if (!format) return null;
  const label = { ubl: 'UBL', cii: 'CII', zugferd: 'ZUGFeRD', fatturapa: 'FatturaPA', 'ksef-fa3': 'FA(3)', facturae: 'Facturae', 'nav-online': 'NAV' }[format] || format;
  return <Chip tone="violet" isDarkMode={isDarkMode}>{label}</Chip>;
};

export const Card = ({ isDarkMode, className = '', children }) => (
  <div className={`rounded-2xl p-5 ${isDarkMode ? 'bg-white/[0.06] border border-white/[0.12]' : 'bg-white border border-slate-200 shadow-sm'} ${className}`}>
    {children}
  </div>
);

export const SectionTitle = ({ isDarkMode, children, right }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{children}</h3>
    {right}
  </div>
);

/** KPI-Kachel — Aufgaben zuerst (Basware-Muster): Zahl gross, Klick filtert die Liste. */
export const KpiTile = ({ isDarkMode, label, value, tone = 'slate', onClick, Icon }) => {
  const toneText = {
    blue: isDarkMode ? 'text-blue-300' : 'text-blue-600',
    amber: isDarkMode ? 'text-amber-300' : 'text-amber-600',
    red: isDarkMode ? 'text-red-300' : 'text-red-600',
    green: isDarkMode ? 'text-emerald-300' : 'text-emerald-600',
    slate: isDarkMode ? 'text-white/80' : 'text-slate-800',
  }[tone];
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      // min-w-0: Grid-Items shrinken sonst nicht -> ein langes Label (z. B. "Validierungsfehler")
      // sprengt sonst die Kachel und damit die ganze Seite auf dem Smartphone breiter als der Viewport.
      className={`text-left rounded-2xl p-4 min-w-0 transition-all ${isDarkMode
        ? 'bg-white/[0.06] border border-white/[0.12] hover:bg-white/10'
        : 'bg-white border border-slate-200 shadow-sm hover:border-slate-300'} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />}
        <span className={`text-xs leading-tight break-words min-w-0 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${toneText}`}>{value}</div>
    </button>
  );
};

export const money = (v, currency = 'EUR') =>
  Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency });

export const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE');
};

export const inputCls = (isDarkMode) => `px-3 py-2 rounded-xl text-sm outline-none transition-colors ${isDarkMode
  ? 'bg-white/10 border border-white/15 text-white placeholder-white/40 focus:border-violet-400'
  : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-violet-500'}`;

// min-h-[40px] + inline-flex-Zentrierung: garantiert ein fingerfreundliches Tap-Ziel (>=40px),
// auch fuer icon-only-Buttons (Refresh/Download/Plus) — Padding allein bleibt sonst ~32px hoch.
export const btnPrimary = (isDarkMode) => `inline-flex items-center justify-center min-h-[40px] px-4 py-2 rounded-xl text-sm font-medium transition-all ${isDarkMode
  ? 'bg-violet-600 hover:bg-violet-500 text-white'
  : 'bg-violet-600 hover:bg-violet-700 text-white'}`;

export const btnGhost = (isDarkMode) => `inline-flex items-center justify-center min-h-[40px] px-4 py-2 rounded-xl text-sm font-medium transition-all ${isDarkMode
  ? 'bg-white/10 hover:bg-white/15 text-white/80 border border-white/10'
  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`;
